import { useTranslations } from "next-intl";
import {
	useCallback,
	useEffect,
	useMemo,
	useReducer,
	useRef,
	useState,
} from "react";
import { useRouter } from "@/i18n/navigation";
import {
	AIBuilderService,
	DEMO_LIMIT_CODES,
} from "@/lib/ai-builder/ai-service";

import type { ChatMessage, FormSchema } from "@/lib/ai-builder/types";
import {
	checkForDuplicateSchema,
	generateSessionId,
} from "@/lib/ai-builder/utils";
import { normalizeAiSchema } from "@/lib/forms";

interface AiBuilderState {
	sessionId: string | null;
	messages: ChatMessage[];
	input: string;
	isLoading: boolean;
	forms: FormSchema[];
	activeFormId: string | null;
	isStreaming: boolean;
	streamedContent: string;
	streamError: string | null;
	showJsonModal: boolean;
}

type AiBuilderAction =
	| { type: "SET_SESSION_ID"; payload: string }
	| { type: "ADD_MESSAGE"; payload: ChatMessage }
	| { type: "SET_MESSAGES"; payload: ChatMessage[] }
	| { type: "SET_INPUT"; payload: string }
	| { type: "SET_LOADING"; payload: boolean }
	| { type: "ADD_FORM"; payload: FormSchema }
	| { type: "SET_ACTIVE_FORM_ID"; payload: string | null }
	| { type: "SET_STREAMING"; payload: boolean }
	| { type: "SET_STREAMED_CONTENT"; payload: string }
	| { type: "SET_STREAM_ERROR"; payload: string | null }
	| { type: "SET_SHOW_JSON_MODAL"; payload: boolean }
	| { type: "RESET_STREAM_STATE" };

const initialState: AiBuilderState = {
	sessionId: null,
	messages: [],
	input: "",
	isLoading: false,
	forms: [],
	activeFormId: null,
	isStreaming: false,
	streamedContent: "",
	streamError: null,
	showJsonModal: false,
};

function aiBuilderReducer(
	state: AiBuilderState,
	action: AiBuilderAction
): AiBuilderState {
	switch (action.type) {
		case "SET_SESSION_ID":
			return { ...state, sessionId: action.payload };
		case "ADD_MESSAGE":
			return { ...state, messages: [...state.messages, action.payload] };
		case "SET_MESSAGES":
			return { ...state, messages: action.payload };
		case "SET_INPUT":
			return { ...state, input: action.payload };
		case "SET_LOADING":
			return { ...state, isLoading: action.payload };
		case "ADD_FORM":
			return { ...state, forms: [...state.forms, action.payload] };
		case "SET_ACTIVE_FORM_ID":
			return { ...state, activeFormId: action.payload };
		case "SET_STREAMING":
			return { ...state, isStreaming: action.payload };
		case "SET_STREAMED_CONTENT":
			return { ...state, streamedContent: action.payload };
		case "SET_STREAM_ERROR":
			return { ...state, streamError: action.payload };
		case "SET_SHOW_JSON_MODAL":
			return { ...state, showJsonModal: action.payload };
		case "RESET_STREAM_STATE":
			return { ...state, streamedContent: "", streamError: null };
		default:
			return state;
	}
}

/** Текст ошибки отправки: служебные коды переводим, сообщение сервера показываем как есть. */
function resolveSendError(error: string, t: (key: string) => string): string {
	if (error === "no_response") {
		return t("errors.noResponse");
	}
	if (error === "send_failed") {
		return t("errors.sendFailed");
	}
	if (error === "rate_limited") {
		return t("errors.rateLimited");
	}
	return error || t("errors.sendFailed");
}

export interface AiBuilderOptions {
	/** Демо-режим главной (t_4c03e049): запрос с заголовком демо и дневным лимитом вместо премиума. */
	demo?: boolean;
	/** Дневной лимит демо исчерпан: "anon" — показать регистрацию, "user" — текст лимита. */
	onDemoLimitReached?: (kind: "anon" | "user") => void;
	/** Исчерпан лимит запросов: показать модалку с формой заявки (t_df8944fa). */
	onRateLimited?: () => void;
}

export const useAIBuilder = (
	initialPrompt?: string,
	options?: AiBuilderOptions
) => {
	const router = useRouter();
	const t = useTranslations("aiBuilder");
	const [state, dispatch] = useReducer(aiBuilderReducer, initialState);
	const demo = options?.demo ?? false;
	const onDemoLimitReached = options?.onDemoLimitReached;
	const onRateLimited = options?.onRateLimited;

	// Прикреплённый к сообщению документ (скрепка в чате, t_aba782d3)
	const [attachedFile, setAttachedFile] = useState<File | null>(null);

	const messagesEndRef = useRef<HTMLDivElement>(null);
	const streamingRef = useRef<HTMLDivElement>(null);
	const initialPromptProcessedRef = useRef(false);

	const processInitialPrompt = useCallback(() => {
		if (initialPromptProcessedRef.current) return;

		const urlParams = new URLSearchParams(window.location.search);
		const promptParam = urlParams.get("prompt");
		const sentParam = urlParams.get("sent");

		if (promptParam && sentParam === "true") {
			const decodedPrompt = decodeURIComponent(promptParam);
			dispatch({ type: "SET_INPUT", payload: decodedPrompt });
			initialPromptProcessedRef.current = true;

			setTimeout(() => {
				autoSendPrompt(decodedPrompt);
			}, 100);
		} else if (initialPrompt) {
			dispatch({ type: "SET_INPUT", payload: initialPrompt });
			initialPromptProcessedRef.current = true;

			setTimeout(() => {
				autoSendPrompt(initialPrompt);
			}, 100);
		}
	}, [initialPrompt]);

	useEffect(() => {
		processInitialPrompt();
	}, [processInitialPrompt]);

	const scrollToBottom = useCallback(() => {
		messagesEndRef.current?.scrollIntoView({ behavior: "smooth" });
	}, []);

	const scrollStreamingToBottom = useCallback(() => {
		if (state.isStreaming && streamingRef.current) {
			streamingRef.current.scrollTop = streamingRef.current.scrollHeight;
		}
	}, [state.isStreaming]);

	const processAiResponse = useCallback(
		async (
			promptText: string,
			currentMessages: ChatMessage[],
			file?: File | null
		) => {
			const currentSessionId = state.sessionId || generateSessionId();
			dispatch({ type: "SET_SESSION_ID", payload: currentSessionId });

			dispatch({ type: "SET_LOADING", payload: true });
			dispatch({ type: "SET_STREAMING", payload: true });
			dispatch({ type: "RESET_STREAM_STATE" });

			// Причину отказа сервера запоминаем, чтобы не подменить её общим текстом ниже
			let sendError: string | null = null;

			const { fullText, foundJson } = await AIBuilderService.sendMessage(
				currentMessages,
				currentSessionId,
				(content: string) =>
					dispatch({ type: "SET_STREAMED_CONTENT", payload: content }),
				(error: string) => {
					sendError = error;
					// Исчерпан лимит: наружу сигнал, а вместо текста ошибки — модалка
					// с формой заявки на расширение (t_df8944fa).
					if (error === "rate_limited") {
						onRateLimited?.();
						return;
					}
					// Код дневного лимита демо — не текст ошибки, а сигнал наружу
					// (сообщение сервера англоязычное, а нужен UI: модалка или локализованный текст)
					if (DEMO_LIMIT_CODES.has(error)) {
						return;
					}
					dispatch({
						type: "SET_STREAM_ERROR",
						payload: resolveSendError(error, t),
					});
				},
				file,
				{ demo }
			);

			dispatch({ type: "SET_STREAMING", payload: false });
			dispatch({ type: "SET_LOADING", payload: false });

			if (sendError && DEMO_LIMIT_CODES.has(sendError)) {
				onDemoLimitReached?.(
					sendError === "demo_limit_anon" ? "anon" : "user"
				);
				return;
			}

			if (foundJson) {
				const normalizedSchema = normalizeAiSchema(foundJson);
			const existing = checkForDuplicateSchema(state.forms, normalizedSchema);
				if (existing) {
					dispatch({ type: "SET_ACTIVE_FORM_ID", payload: existing.id });
					dispatch({
						type: "ADD_MESSAGE",
						payload: {
							role: "assistant",
							content:
								t("errors.formExists"),
							schema: normalizedSchema,
						},
					});
				} else {
					const newId = Date.now().toString();
					const newForm = { id: newId, schema: normalizedSchema, prompt: promptText };
					dispatch({ type: "ADD_FORM", payload: newForm });
					dispatch({ type: "SET_ACTIVE_FORM_ID", payload: newId });
					dispatch({
						type: "ADD_MESSAGE",
						payload: {
							role: "assistant",
							content: fullText,
							schema: normalizedSchema,
						},
					});
				}
				dispatch({ type: "SET_STREAMED_CONTENT", payload: "" });
			} else {
				dispatch({
					type: "SET_STREAM_ERROR",
					payload: sendError
						? resolveSendError(sendError, t)
						: t("errors.generateFailed"),
				});
			}
		},
		[state.sessionId, state.forms, t, demo, onDemoLimitReached, onRateLimited]
	);

	const autoSendPrompt = useCallback(
		async (promptText: string) => {
			if (!promptText.trim()) return;

			const newMessage: ChatMessage = { role: "user", content: promptText };
			dispatch({ type: "ADD_MESSAGE", payload: newMessage });
			await processAiResponse(promptText, [...state.messages, newMessage]);
		},
		[state.messages, processAiResponse]
	);

	const handleSend = useCallback(
		async (e: React.FormEvent<HTMLFormElement>) => {
			e.preventDefault();
			const file = attachedFile;
			const trimmedInput = state.input.trim();
			// Отправка возможна и с одним документом, без текста
			if (!(trimmedInput || file)) {
				return;
			}

			const currentInput =
				trimmedInput || (file ? t("input.documentPrompt") : "");
			dispatch({ type: "SET_INPUT", payload: "" });

			const newMessage: ChatMessage = {
				role: "user",
				content: currentInput,
				attachment: file
					? { name: file.name, size: file.size }
					: undefined,
			};
			dispatch({ type: "ADD_MESSAGE", payload: newMessage });
			setAttachedFile(null);
			await processAiResponse(currentInput, [...state.messages, newMessage], file);
		},
		[state.input, state.messages, processAiResponse, attachedFile, t]
	);

	const handleUseForm = useCallback(() => {
		const activeForm = state.forms.find((f) => f.id === state.activeFormId);
		if (activeForm?.schema) {
			localStorage.setItem(
				"importedFormSchema",
				JSON.stringify(activeForm.schema)
			);
			router.push("/form-builder");
		}
	}, [state.forms, state.activeFormId, router]);

	const activeForm = useMemo(
		() => state.forms.find((f) => f.id === state.activeFormId),
		[state.forms, state.activeFormId]
	);

	const actions = useMemo(
		() => ({
			setInput: (value: string) =>
				dispatch({ type: "SET_INPUT", payload: value }),
			setActiveFormId: (id: string | null) =>
				dispatch({ type: "SET_ACTIVE_FORM_ID", payload: id }),
			setStreamedContent: (content: string) =>
				dispatch({ type: "SET_STREAMED_CONTENT", payload: content }),
			setStreamError: (error: string | null) =>
				dispatch({ type: "SET_STREAM_ERROR", payload: error }),
			setShowJsonModal: (show: boolean) =>
				dispatch({ type: "SET_SHOW_JSON_MODAL", payload: show }),
		}),
		[]
	);

	return {
		messages: state.messages,
		input: state.input,
		attachedFile,
		setAttachedFile,
		isLoading: state.isLoading,
		forms: state.forms,
		activeFormId: state.activeFormId,
		isStreaming: state.isStreaming,
		streamedContent: state.streamedContent,
		streamError: state.streamError,
		showJsonModal: state.showJsonModal,
		activeForm,

		messagesEndRef,
		streamingRef,

		...actions,
		handleSend,
		handleUseForm,

		processInitialPrompt,
		scrollToBottom,
		scrollStreamingToBottom,
	};
};
